import React, { useState } from 'react';
import { Button, DatePicker, Dropdown, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { resellerService } from '../../services/reseller.service';

const { RangePicker } = DatePicker;

interface MonthlyRedemptionsCsvExportProps {
  resellerId: number;
  resellerCode?: string;
  buttonType?: 'default' | 'primary';
  showText?: boolean;
}

const MonthlyRedemptionsCsvExport: React.FC<MonthlyRedemptionsCsvExportProps> = ({ 
  resellerId,
  resellerCode,
  buttonType = 'default',
  showText = true 
}) => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDownload = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Please select a date range');
      return;
    }

    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');

    if (dateRange[0].isAfter(dateRange[1])) {
      message.error('Start date must be before end date');
      return;
    }

    if (dateRange[0].year() < 2000 || dateRange[1].year() > 2100) {
      message.error('Year must be between 2000 and 2100');
      return;
    }

    setLoading(true);
    try {
      const blob = await resellerService.downloadMonthlyCsv(resellerId, startDate, endDate);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const startFormatted = dateRange[0].format('MM-DD-YYYY');
      const endFormatted = dateRange[1].format('MM-DD-YYYY');
      const code = resellerCode || `reseller_${resellerId}`;
      link.download = `${code}-redemptions-${startFormatted}-${endFormatted}.csv`;
      
      document.body.appendChild(link);
      link.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      message.success('CSV file downloaded successfully');
      setDropdownOpen(false);
      setDateRange(null);
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message.error(errorData.message || 'Failed to download CSV');
        } catch {
          message.error('Failed to download CSV');
        }
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to download CSV. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current: Dayjs) => {
    return current && (current.isAfter(dayjs(), 'day') || current.year() < 2000 || current.year() > 2100);
  };

  const dropdownContent = (
    <div className="csv-export-dropdown" onClick={(e) => e.stopPropagation()}>
      <div className="csv-export-dropdown__header">
        <strong>Export Date Range Report</strong>
        <p>Select start and end dates for CSV</p>
      </div>
      <div className="csv-export-dropdown__content">
        <RangePicker
          placeholder={['Start date', 'End date']}
          value={dateRange}
          onChange={setDateRange}
          disabledDate={disabledDate}
          format="YYYY-MM-DD"
          style={{ width: '100%', marginBottom: 12 }}
        />
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          loading={loading}
          disabled={!dateRange || !dateRange[0] || !dateRange[1]}
          block
        >
          Download CSV
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      placement="bottomRight"
    >
      <Button type={buttonType} icon={<DownloadOutlined />}>
        {showText ? 'Export CSV' : null}
      </Button>
    </Dropdown>
  );
};

export default MonthlyRedemptionsCsvExport;
